/**
 * CFCalculatorModal — "🧮 Calcola CF" widget for the DealerCustomerForm.
 *
 * Opens a small modal that collects the 6 fields required to synthesize
 * an Italian Codice Fiscale (nome, cognome, sesso, data di nascita,
 * paese di nascita, città/comune di nascita) and calls the backend
 * `POST /api/tools/generate-cf` endpoint. The dealer/seller can then
 * one-click paste the result into the main form's tax_code input.
 *
 * The generated CF is labelled clearly as "valore indicativo" — the
 * seller must always verify against the customer's tessera sanitaria.
 *
 * Req. Fabrizio 27/07/2026 — CF inversa per ridurre errori di battitura
 * durante l'inserimento manuale del cliente.
 */
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { X, Calculator, Copy, AlertCircle, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import { CountryCombobox } from "../shared/CountryCombobox";
import { dealerApi } from "../../utils/dealerApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  IT: {
    title: "Calcola Codice Fiscale",
    subtitle: "Inserisci i dati anagrafici del cliente. Il CF verrà generato a scopo indicativo.",
    firstName: "Nome",
    lastName: "Cognome",
    gender: "Sesso",
    male: "Maschio",
    female: "Femmina",
    birthDate: "Data di nascita",
    bornAbroad: "Nato all'estero",
    birthCountry: "Paese di nascita",
    birthPlace: "Comune di nascita",
    birthPlaceForeign: "Città di nascita (informativa)",
    birthPlaceHint: "es. Roma, Milano, Torino",
    calculate: "Calcola",
    calculating: "Calcolo in corso...",
    resultLabel: "Codice Fiscale generato",
    disclaimer: "Valore indicativo — verifica sempre con la tessera sanitaria del cliente.",
    use: "Usa questo CF",
    close: "Chiudi",
    copy: "Copia",
    copied: "Copiato negli appunti",
    error: "Errore di generazione",
  },
  UK: {
    title: "Calculate Codice Fiscale",
    subtitle: "Enter the customer's identity data. The CF will be generated for reference.",
    firstName: "First Name",
    lastName: "Last Name",
    gender: "Gender",
    male: "Male",
    female: "Female",
    birthDate: "Date of Birth",
    bornAbroad: "Born abroad",
    birthCountry: "Country of Birth",
    birthPlace: "City of Birth (Italian)",
    birthPlaceForeign: "City of Birth (informative)",
    birthPlaceHint: "e.g. Roma, Milano, Torino",
    calculate: "Calculate",
    calculating: "Calculating...",
    resultLabel: "Generated Codice Fiscale",
    disclaimer: "Indicative value — always verify with the customer's health card.",
    use: "Use this CF",
    close: "Close",
    copy: "Copy",
    copied: "Copied to clipboard",
    error: "Generation error",
  },
};

const getT = (lang) => T[(lang || "IT").toUpperCase()] || T.IT;

export const CFCalculatorModal = ({
  open,
  onClose,
  onUseCF,
  dealerToken,
  lang = "IT",
  prefillFirstName = "",
  prefillLastName = "",
}) => {
  const t = getT(lang);
  const [inputs, setInputs] = useState({
    first_name: prefillFirstName,
    last_name: prefillLastName,
    gender: "M",
    birth_date: "",
    birth_country: "IT",
    birth_place: "",
  });
  const [isForeignBirth, setIsForeignBirth] = useState(false);
  const [result, setResult] = useState(null); // {codice_fiscale, birth_place_resolved}
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Reset state when the modal is (re-)opened
  useEffect(() => {
    if (open) {
      setInputs({
        first_name: prefillFirstName,
        last_name: prefillLastName,
        gender: "M",
        birth_date: "",
        birth_country: "IT",
        birth_place: "",
      });
      setIsForeignBirth(false);
      setResult(null);
      setError(null);
    }
  }, [open, prefillFirstName, prefillLastName]);

  if (!open) return null;

  // The form is "complete enough" to calculate when all required
  // fields for the current context are filled. Foreign births require
  // birth_country but not birth_place; IT births require birth_place.
  const isComplete =
    inputs.first_name.trim().length >= 2 &&
    inputs.last_name.trim().length >= 2 &&
    (inputs.gender === "M" || inputs.gender === "F") &&
    /^\d{4}-\d{2}-\d{2}$/.test(inputs.birth_date) &&
    (isForeignBirth ? Boolean(inputs.birth_country) : Boolean(inputs.birth_place.trim()));

  const handleCalculate = async () => {
    if (!isComplete) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload = {
        first_name: inputs.first_name.trim(),
        last_name: inputs.last_name.trim(),
        gender: inputs.gender,
        birth_date: inputs.birth_date,
        birth_place: isForeignBirth ? "" : inputs.birth_place.trim(),
        birth_country: isForeignBirth ? inputs.birth_country : "IT",
      };
      const res = await dealerApi.post(`/tools/generate-cf`, { token: dealerToken }, payload);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleUse = () => {
    if (!result?.codice_fiscale) return;
    onUseCF(result.codice_fiscale);
    onClose();
  };

  const handleCopy = async () => {
    if (!result?.codice_fiscale) return;
    try {
      await navigator.clipboard.writeText(result.codice_fiscale);
      toast.success(t.copied, { duration: 1500 });
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  const inputCls =
    "w-full mt-1 px-3 py-2 bg-[#111] border border-gray-700 rounded-lg text-white text-sm focus:border-lojack-red focus:outline-none disabled:opacity-40";

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[210] flex items-center justify-center p-4"
      onClick={(e) => e.stopPropagation()}
      data-testid="cf-calculator-overlay"
    >
      <div
        className="bg-[#1A1A1A] rounded-2xl w-full max-w-md border border-gray-700 overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="cf-calculator-modal"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Calculator size={18} className="text-lojack-red" />
            <h2 className="text-base font-bold text-white">{t.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-800 rounded-lg"
            data-testid="cf-calculator-close"
            aria-label={t.close}
          >
            <X size={18} className="text-gray-400" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto">
          <p className="text-gray-400 text-xs">{t.subtitle}</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs">{t.firstName}*</label>
              <input
                value={inputs.first_name}
                onChange={(e) => setInputs({ ...inputs, first_name: e.target.value })}
                className={inputCls}
                data-testid="cf-calc-first-name"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs">{t.lastName}*</label>
              <input
                value={inputs.last_name}
                onChange={(e) => setInputs({ ...inputs, last_name: e.target.value })}
                className={inputCls}
                data-testid="cf-calc-last-name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs">{t.gender}*</label>
              <select
                value={inputs.gender}
                onChange={(e) => setInputs({ ...inputs, gender: e.target.value })}
                className={inputCls}
                data-testid="cf-calc-gender"
              >
                <option value="M">{t.male}</option>
                <option value="F">{t.female}</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs">{t.birthDate}*</label>
              <input
                type="date"
                value={inputs.birth_date}
                onChange={(e) => setInputs({ ...inputs, birth_date: e.target.value })}
                max={new Date().toISOString().split("T")[0]}
                min="1900-01-01"
                className={inputCls}
                data-testid="cf-calc-birth-date"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="cf-calc-born-abroad"
              checked={isForeignBirth}
              onChange={(e) => {
                setIsForeignBirth(e.target.checked);
                setInputs((prev) => ({
                  ...prev,
                  birth_country: e.target.checked
                    ? prev.birth_country && prev.birth_country !== "IT"
                      ? prev.birth_country
                      : "US"
                    : "IT",
                  birth_place: e.target.checked ? "" : prev.birth_place,
                }));
              }}
              className="w-4 h-4 rounded border-gray-700 bg-[#111] text-lojack-red focus:ring-lojack-red cursor-pointer"
              data-testid="cf-calc-born-abroad"
            />
            <label
              htmlFor="cf-calc-born-abroad"
              className="text-gray-300 text-xs cursor-pointer select-none"
            >
              {t.bornAbroad}
            </label>
          </div>

          {isForeignBirth ? (
            <div>
              <label className="text-gray-400 text-xs">{t.birthCountry}*</label>
              {/* Searchable — Fabrizio 24/07/2026 fix Bohdan bug */}
              <CountryCombobox
                value={inputs.birth_country}
                onChange={(code) => setInputs({ ...inputs, birth_country: code || 'US' })}
                lang={lang}
                excludeCode="IT"
                required
                testId="cf-calc-birth-country"
              />
            </div>
          ) : (
            <div>
              <label className="text-gray-400 text-xs">{t.birthPlace}*</label>
              <input
                value={inputs.birth_place}
                onChange={(e) => setInputs({ ...inputs, birth_place: e.target.value })}
                placeholder={t.birthPlaceHint}
                className={inputCls}
                data-testid="cf-calc-birth-place"
              />
            </div>
          )}

          <button
            onClick={handleCalculate}
            disabled={!isComplete || loading}
            className="w-full mt-2 btn-lojack flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed py-2.5"
            data-testid="cf-calc-submit"
          >
            <Calculator size={14} />
            {loading ? t.calculating : t.calculate}
          </button>

          {error && (
            <div
              className="flex items-start gap-2 p-3 bg-red-900/30 border border-red-800 rounded-lg"
              data-testid="cf-calc-error"
            >
              <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-xs">{error}</p>
            </div>
          )}

          {result && (
            <div
              className="mt-2 border border-lojack-red rounded-xl overflow-hidden"
              data-testid="cf-calc-result"
            >
              <div className="bg-gradient-to-r from-red-900/40 to-black p-4">
                <p className="text-gray-400 text-[10px] uppercase tracking-wider">
                  {t.resultLabel}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <p
                    className="font-mono text-2xl font-bold text-white tracking-wider"
                    data-testid="cf-calc-result-value"
                  >
                    {result.codice_fiscale}
                  </p>
                  <button
                    onClick={handleCopy}
                    className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                    title={t.copy}
                    data-testid="cf-calc-copy"
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>
              <div className="bg-[#111] p-3 border-t border-gray-800 flex items-start gap-2">
                <Info size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-400 text-[11px] leading-relaxed">{t.disclaimer}</p>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 flex gap-3 border-t border-gray-700 bg-[#111]">
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 border border-gray-600 rounded-lg text-gray-400 hover:bg-gray-800 text-sm transition-colors"
            data-testid="cf-calc-cancel"
          >
            {t.close}
          </button>
          <button
            onClick={handleUse}
            disabled={!result?.codice_fiscale}
            className="flex-1 btn-lojack flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
            data-testid="cf-calc-use"
          >
            <CheckCircle2 size={14} />
            {t.use}
          </button>
        </div>
      </div>
    </div>
  );
};

CFCalculatorModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUseCF: PropTypes.func.isRequired,
  dealerToken: PropTypes.string.isRequired,
  lang: PropTypes.string,
  prefillFirstName: PropTypes.string,
  prefillLastName: PropTypes.string,
};

export default CFCalculatorModal;
